import { Box, Button, LinearProgress } from "@mui/material";
import { UploadIcon } from "../../Icons/Icons";
import { useEffect, useState } from "react";

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const SIZE_ERROR_MESSAGE =
  "This photo is larger than 10 MB. Please choose a smaller image.";

interface FileUploadProps {
  isFileUploading: boolean;
  selectedFile: any;
  showCancel?: boolean;
  onFileChange: (file: any) => void;
  onCancelUpload: () => void;
  handleFileSubmission?: (event: any) => void;
  modelType: string;
  allowMultiple?: boolean; // New prop to control multiple file selection
}

const FileUpload = (props: FileUploadProps) => {
  const {
    isFileUploading,
    selectedFile,
    showCancel = true,
    onFileChange,
    onCancelUpload,
    handleFileSubmission,
    modelType,
    allowMultiple,
  } = props;

  const [isMobile, setIsMobile] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const applySelectedFiles = (files: File[]) => {
    if (files.length === 0) return;
    if (files.some((file) => file.size > MAX_UPLOAD_BYTES)) {
      setSizeError(SIZE_ERROR_MESSAGE);
      onFileChange(allowMultiple ? [] : null);
      return;
    }
    setSizeError(null);
    onFileChange(allowMultiple ? files : files[0]);
  };

  const stopDefaults = (event: React.DragEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const dragEvents = {
    onDragOver: stopDefaults,
    onDrop: (event: React.DragEvent<HTMLElement>) => {
      stopDefaults(event);
      if (event.dataTransfer.files.length > 0) {
        const dropped = Array.from(event.dataTransfer.files);
        applySelectedFiles(allowMultiple ? dropped : dropped.slice(0, 1));
      }
    },
  };

  const handleChange = (event: any) => {
    const selected = Array.from(event?.target?.files || []) as File[];
    applySelectedFiles(allowMultiple ? selected : selected.slice(0, 1));
    event.target.value = "";
  };

  const getFileDisplayText = () => {
    if (!selectedFile) return "";
    
    if (Array.isArray(selectedFile)) {
      if (selectedFile.length === 1) {
        return selectedFile[0].name;
      } else {
        return `${selectedFile.length} files selected`;
      }
    }
    
    return selectedFile.name;
  };

  const uploader = (
    <>
      <input
        onChange={handleChange}
        accept="image/png, image/jpeg"
        className="file-upload"
        id="fileUpload"
        type="file"
        capture={undefined}
        multiple={allowMultiple}
      />

      {isMobile ? (
        <div className="mobile-upload-buttons">
          <input
            onChange={handleChange}
            accept="image/png, image/jpeg"
            className="file-upload"
            id="takePhoto"
            type="file"
            capture="environment"
            multiple={allowMultiple}
          />
          <input
            onChange={handleChange}
            accept="image/png, image/jpeg"
            className="file-upload"
            id="uploadPhoto"
            type="file"
            multiple={allowMultiple}
          />
          <label htmlFor="takePhoto" className="mobile-button">
            <Box>
              <img src={`/images/FileUpload_${modelType}.png`} alt={`${modelType}.png`} />
              <p>Take a photo</p>
            </Box>
          </label>
          <label htmlFor="uploadPhoto" className="mobile-button">
            <Box>
              <img src={`/images/FileUpload_${modelType}.png`} alt={`${modelType}.png`} />
              <p>Upload {allowMultiple ? "photos" : "a photo"}</p>
            </Box>
          </label>
        </div>
      ) : (
        <label htmlFor="fileUpload" {...dragEvents}>
          <Box>
            <img src={`/images/FileUpload_${modelType}.png`} alt={`${modelType}.png`} />
            <p>Drag & drop {allowMultiple ? "images" : "an image"}</p>
            <span>Supported formats: JPG, PNG. Max size: 10 MB.</span>
            {allowMultiple && <span><br/>You can select multiple files.</span>}
          </Box>
        </label>
      )}
    </>
  );

  const loader = (
    <div className="loader-wrapper">
      <h3>Uploading</h3>
      <div className="loader">
        <p>{getFileDisplayText()}</p>
        <Box sx={{ width: "100%" }}>
          <LinearProgress
            classes={{
              colorPrimary: "progress-bg",
              bar: "progress-bar",
            }}
          />
        </Box>
      </div>
      <Button
        variant="outlined"
        className="btn btn-outlined"
        onClick={onCancelUpload}
      >
        Cancel
      </Button>
    </div>
  );

  const controls = (
    <>
      <div className="row">
        <Button
          variant="contained"
          className="btn btn-blue"
          disabled={!selectedFile || (Array.isArray(selectedFile) && selectedFile.length === 0)}
          onClick={handleFileSubmission}
          title="Click to upload the selected image(s)"
        >
          <UploadIcon
            color={!selectedFile || (Array.isArray(selectedFile) && selectedFile.length === 0) ? "rgba(0, 0, 0, 0.12)" : "#fff"}
          />
          Upload {allowMultiple && Array.isArray(selectedFile) && selectedFile.length > 1 ? "Images" : "Image"}
        </Button>
      </div>
      {showCancel && (
        <div className="row">
          <Button
            variant="outlined"
            className="btn btn-outlined"
            onClick={onCancelUpload}
          >
            Cancel
          </Button>
        </div>
      )}
    </>
  );

  return (
    <div className="file-upload-wrapper">
      <div className="row">{uploader}</div>
      {sizeError && (
        <div className="row">
          <p className="file-size-error" role="alert">
            {sizeError}
          </p>
        </div>
      )}
      {!isFileUploading && <div className="row">{controls}</div>}
      {isFileUploading && <div className="row">{loader}</div>}
    </div>
  );
};

export default FileUpload;
